/**
 * Termos de Serviço — página pública, sem login.
 *
 * Item exigido pela Meta para publicar o app, ao lado da política de
 * privacidade. Mas serve principalmente ao cliente: ele está conectando a
 * própria conta do Instagram e as próprias credenciais da Shopee a um sistema
 * que publica em nome dele. O que pode e o que não pode acontecer com isso
 * precisa estar escrito antes, não descoberto depois.
 *
 * O tom é o mesmo da política: português claro, sem parágrafo decorativo. Cada
 * limite descrito aqui existe de fato no sistema.
 */

const ATUALIZADA_EM = '26 de agosto de 2026';
const CONTATO = 'gustavo@gugacloud.com.br';

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="doc-secao">
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}

export function Termos({ onVoltar }: { onVoltar: () => void }) {
  return (
    <div className="landing">
      <div className="doc-pagina">
        <button type="button" className="ghost doc-voltar" onClick={onVoltar}>
          ← Voltar
        </button>

        <p className="doc-eyebrow mono">Automação Shopee</p>
        <h1 className="doc-titulo">Termos de Serviço</h1>
        <p className="muted">Atualizados em {ATUALIZADA_EM}</p>

        <Secao titulo="O que você está contratando">
          <p>
            A <strong>Automação Shopee</strong> é uma ferramenta que ajuda afiliados da
            Shopee a divulgar produtos: encontra ofertas na API oficial de afiliados,
            gera textos, agenda publicações e publica em Instagram, TikTok e WhatsApp,
            além de mostrar quanto cada canal rendeu em comissão.
          </p>
          <p>
            O serviço é operado por <strong>GugaCloud</strong>. Ao criar uma conta, você
            concorda com estes termos.
          </p>
        </Secao>

        <Secao titulo="Sua conta">
          <ul>
            <li>Você precisa ter 18 anos ou mais.</li>
            <li>
              As credenciais de acesso são suas e intransferíveis. Você responde pelo que
              acontece na sua conta.
            </li>
            <li>
              Você é responsável por manter válidas as conexões com Shopee, Instagram e
              WhatsApp. Se um token expira ou é revogado, o sistema para de publicar por
              aquele canal e avisa no painel.
            </li>
          </ul>
        </Secao>

        <Secao titulo="O que o sistema publica em seu nome">
          <p>
            O sistema publica <strong>o que você programou</strong>: os produtos que você
            selecionou, no texto que você aprovou, nos horários que você definiu. Nada é
            publicado sem que exista um agendamento seu.
          </p>
          <p>
            Textos gerados por inteligência artificial são <strong>sugestões</strong>. Eles
            ficam em um campo editável antes de virar publicação, e a revisão é sua. Você
            é o responsável pelo conteúdo que sai no seu perfil — inclusive pela
            identificação de publicidade, que o sistema insere automaticamente mas que
            você pode alterar.
          </p>
        </Secao>

        <Secao titulo="Uso aceitável">
          <p>Ao usar o serviço, você concorda em não:</p>
          <ul>
            <li>
              Publicar conteúdo ilegal, enganoso, ofensivo, ou que viole direitos de
              terceiros.
            </li>
            <li>
              Anunciar preço, desconto ou condição que não corresponda à oferta real na
              Shopee.
            </li>
            <li>
              Usar a automação de respostas para enviar mensagem a quem não pediu. A
              resposta privada só existe como reação a um comentário público da própria
              pessoa.
            </li>
            <li>
              Conectar contas de terceiros sem autorização, ou usar o serviço para operar
              perfis que não são seus ou de clientes seus.
            </li>
            <li>
              Tentar contornar limites técnicos, extrair dados em massa do sistema ou
              interferir na operação de outros clientes.
            </li>
          </ul>
          <p>
            Você também concorda em respeitar os termos da Shopee, da Meta (Instagram) e
            do WhatsApp. O descumprimento das regras dessas plataformas pode levar à
            suspensão da sua conta lá, o que está fora do nosso controle.
          </p>
        </Secao>

        <Secao titulo="O que NÃO prometemos">
          <p>
            Esta seção existe para você não descobrir depois. Somos honestos sobre os
            limites:
          </p>
          <ul>
            <li>
              <strong>Não garantimos vendas nem comissão.</strong> A ferramenta divulga; o
              resultado depende do produto, do público e do mercado.
            </li>
            <li>
              <strong>Não garantimos que as plataformas continuarão permitindo tudo.</strong>{' '}
              Shopee, Instagram e WhatsApp mudam regras e limites de API sem nos consultar.
              Quando isso afeta uma função, avisamos e adaptamos o que for possível.
            </li>
            <li>
              <strong>Não controlamos bloqueios feitos por essas plataformas.</strong> O
              WhatsApp, em especial, é conectado por dispositivo pareado e pode restringir
              números que enviem muito conteúdo promocional. O sistema espaça os envios
              para reduzir esse risco, mas o risco não é zero e a decisão é da plataforma.
            </li>
            <li>
              <strong>Não garantimos disponibilidade ininterrupta.</strong> Há manutenções
              e há falhas de terceiros. Publicações que falham por causa transitória são
              reagendadas automaticamente; as demais aparecem como falhas no painel.
            </li>
          </ul>
        </Secao>

        <Secao titulo="Seus dados e conteúdo">
          <p>
            As mídias, textos e credenciais que você envia continuam <strong>seus</strong>.
            Nós os usamos apenas para operar o serviço para você, conforme a{' '}
            <a href="/privacidade">Política de Privacidade</a>.
          </p>
          <p>
            Não usamos seu conteúdo para treinar modelos, não o revendemos e não o
            divulgamos a outros clientes.
          </p>
        </Secao>

        <Secao titulo="Pagamento">
          <p>
            Quando houver cobrança, o valor, a periodicidade e a forma de pagamento são
            informados antes da contratação. Alterações de preço são comunicadas com
            antecedência mínima de 30 dias, e você pode encerrar a conta antes que passem
            a valer.
          </p>
        </Secao>

        <Secao titulo="Encerramento">
          <p>
            Você pode encerrar a conta a qualquer momento, escrevendo para{' '}
            <strong>{CONTATO}</strong>. A exclusão dos dados segue o prazo descrito na
            Política de Privacidade.
          </p>
          <p>
            Podemos suspender uma conta que descumpra a seção de uso aceitável, que
            coloque em risco a operação dos demais clientes, ou que exponha o serviço a
            sanções das plataformas. Sempre que possível, avisamos antes e damos prazo
            para correção.
          </p>
        </Secao>

        <Secao titulo="Limitação de responsabilidade">
          <p>
            O serviço é fornecido no estado em que se encontra. Na extensão permitida pela
            lei brasileira, nossa responsabilidade por eventuais prejuízos fica limitada
            ao valor pago por você nos 12 meses anteriores ao fato.
          </p>
          <p>
            Nada aqui afasta direitos que a legislação de consumo garante a você quando
            ela for aplicável.
          </p>
        </Secao>

        <Secao titulo="Mudanças nestes termos">
          <p>
            Se algo mudar, atualizamos esta página e a data no topo. Mudanças relevantes
            são comunicadas por e-mail. Continuar usando o serviço depois disso significa
            concordar com a nova versão.
          </p>
        </Secao>

        <Secao titulo="Lei aplicável e contato">
          <p>
            Estes termos são regidos pela lei brasileira. Dúvidas:{' '}
            <strong>{CONTATO}</strong>
          </p>
        </Secao>
      </div>
    </div>
  );
}
